import { useEffect, useMemo, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';

// import { PlaceObject } from '@eventbrite/consumer-search.discover-utils/types';
// import { FormattedEvent } from '@eventbrite/consumer-search.event-renderer';
import { GlobalHeader, type GlobalHeaderProps } from '@eventbrite/consumer-header.global-header';
import { TagManager } from '@eventbrite/webapp-data';

import { resolveHeaderCreatorVariant, useHeaderCreateEventsSettings } from '@/bsp/config';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { Footer, type FooterProps } from '@/components/Footer';

import { TAG_MANAGER_ENVIRONMENT } from '@/constants';
import { BrowseContainer } from '@/containers/BrowseContainer';
import { HomeBottomShelfContainer } from '@/containers/HomeBottomShelfContainer';
import { getRestOfContext } from '@/redux/root/';
import { DiscoverState } from '@/shared/types';
import type { BaseProps } from '@/types';
import styles from './HomePageContainer.module.scss';
import { HeroBanner } from '@/components/HeroBanner';
import { SplitValues } from '@/utils/split';
import { useIsMobileDevice, getWindowObject } from '@eventbrite/legacy-infra.feature-detection';
import { getQueryParam } from '@/utils/queryString';
import { SMALL_WIDTH } from '@/common/breakpoints';
import { TLD_UNITED_KINGDOM } from '@/shared/constants/constants';
import {
    CompletionType,
    DEFAULT_COMPLETION_TYPES,
} from '@eventbrite/consumer-search.search-bar/constants';
import { AppContextProvider } from '@/common/context';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from '@/components/guides/utils/queryClient';
import type { Guides } from '@/types/guides';

interface OwnProps extends BaseProps {
    guides?: Guides;
    // buckets?: Bucket[];
    // featureFlags?: FeatureFlags;
    splitValues: SplitValues;
    // placeId?: string;
    // latitude?: number;
    // longitude?: number;
    // currentPlace?: string;
    // currentPlaceParent?: string;
    // locations?: {
    //     places: PlaceObject[];
    // };
    // placeType?: string;
    // slug?: string;
    // flatBucket?: {
    //     results: FormattedEvent[];
    // };
    // handleRestOfContext?: Function;
    // originalContext?: object;
    // history?: object;
    // updateHeaderImage?: Function;
    // enable_experimentation?: boolean;
    // should_enable_experimentation?: boolean;
    // isGDPRCountry?: boolean;
    globalHeaderProps: GlobalHeaderProps;
    footerProps: FooterProps;
}
export type HomePageProps = OwnProps & ReduxProps;

export function HomePage(props: HomePageProps) {
    const {
        env,
        stage,
        splitValues,
        globalHeaderProps,
        footerProps,
        handleRestOfContext,
        publicUserId,
    } = props;
    const [hasHydrated, setHasHydrated] = useState(false);
    useEffect(() => {
        setHasHydrated(true);
    }, []);
    const { settings: headerCreateEventsSettings, isLoading: isHeaderCreateEventsLoading } =
        useHeaderCreateEventsSettings();
    const creatorVariant = resolveHeaderCreatorVariant(
        headerCreateEventsSettings,
        isHeaderCreateEventsLoading,
        !hasHydrated,
    );

    const [headerKey, setHeaderKey] = useState(0);

    const isMobileDevice = useIsMobileDevice(SMALL_WIDTH);
    const [chatbotEnabled, setChatbotEnabled] = useState(false);

    // EB-323470: per-user Zendesk help-menu gating. The flag must be evaluated
    // per user, but the homepage SSR HTML is shared-cached, so it can't be baked
    // at SSR. Instead fetch it from the uncached /home/api/zendesk-menu endpoint
    // after hydration. We intentionally do NOT gate on the SSR publicUserId: it is
    // part of the shared-cached payload and is stale, so gating on it would hide
    // the menu for logged-in users served a cache populated by an anonymous
    // request. The endpoint reads the real per-request user and returns false
    // (without a Harness call, so no MTK) when logged out, so it is the
    // authoritative source. Still gated on en_US (eb-lang is in the CDN cache key,
    // so the SSR locale is reliable) to avoid unnecessary requests.
    const [showZendeskMenu, setShowZendeskMenu] = useState(false);
    useEffect(() => {
        if (env.localeInfo.locale !== 'en_US') {
            return;
        }
        let cancelled = false;
        fetch('/home/api/zendesk-menu')
            .then((res) => res.json())
            .then((data) => {
                if (!cancelled) {
                    setShowZendeskMenu(Boolean(data?.showZendeskMenu));
                }
            })
            .catch(() => {
                // Fail closed: keep the legacy Help Center entry.
            });
        return () => {
            cancelled = true;
        };
    }, [env.localeInfo.locale]);

    const isBackForward = () => {
        const navEntries =
            (performance.getEntriesByType('navigation') as PerformanceNavigationTiming[]) || [];
        const nav = navEntries[0];
        return nav && nav.type === 'back_forward';
    };
    const restoredFromBFCache = (e: { persisted: boolean }) => {
        try {
            return Boolean(e?.persisted) || isBackForward();
        } catch (_err) {
            return false;
        }
    };
    const _handlePageShow = (e: { persisted: boolean }) => {
        if (restoredFromBFCache(e)) {
            setHeaderKey((k) => k + 1);
        }
    };

    // Former Statsig experiment `web_searchbar_autocomplete_organizer_v2` decided test —
    // always include organizer autocomplete completions.
    const _searchBarProps = useMemo(() => {
        return {
            chatbotEnabled,
            completionTypes: [...DEFAULT_COMPLETION_TYPES, CompletionType.ORGANIZER],
            pageType: 'Feed',
        };
    }, [chatbotEnabled]);

    useEffect(() => {
        // Former Statsig experiment `nlsearch-chatbot-web` decided control — chatbot off.
        // Keep opt-out (set by @eventbrite/consumer-search.chatbot) and optional
        // `?nlsearch-opt-in=true` for TStaff / manual testing.
        const optOutKey = 'nlsearch-experiment-opt-out';
        const localStorage = getWindowObject('localStorage');
        const optOutValue = localStorage?.getItem(optOutKey);

        // This condition is necessary due to a bug in rendering where the `useIsMobileDevice`
        // hook is not returning the correct value on initial render.
        if (!isMobileDevice || optOutValue === 'true') {
            setChatbotEnabled(false);
            return;
        }

        const overrideFlag = getQueryParam('nlsearch-opt-in', 'false') === 'true';
        setChatbotEnabled(overrideFlag);
    }, [isMobileDevice]);

    useEffect(() => {
        handleRestOfContext();
    }, []);

    // const eventGroups = createEventGroupFixture(consumerCardTestEvent);
    return (
        <main className={styles.root}>
            <AppContextProvider locale={env.localeInfo.locale}>
                <ErrorBoundary componentName="GlobalHeader">
                    <GlobalHeader
                        {...globalHeaderProps}
                        isHomePage={true}
                        creatorVariant={creatorVariant}
                        key={headerKey}
                        featureFlags={{
                            showHelpCenterMenu: splitValues.showHelpCenterMenu,
                            showZendeskMenu,
                        }}
                        searchBar={_searchBarProps}
                    />
                </ErrorBoundary>
                {/* TODO handle seasonality properly */}

                <ErrorBoundary componentName="HeroBanner">
                    <HeroBanner
                        showGuides={!!props.guides}
                        locale={env.localeInfo.locale}
                        initialGuides={props.guides}
                        littleVeniceSpotlightEnabled={
                            splitValues.littleVeniceSpotlightEnabled &&
                            env.tld === TLD_UNITED_KINGDOM
                        }
                    />
                </ErrorBoundary>

                <ErrorBoundary componentName="BrowseContainer">
                    <QueryClientProvider client={queryClient}>
                        <BrowseContainer userId={publicUserId} />
                    </QueryClientProvider>
                </ErrorBoundary>

                <ErrorBoundary componentName="TagManager">
                    <TagManager
                        publicUserId={publicUserId}
                        stage={stage}
                        environment={TAG_MANAGER_ENVIRONMENT}
                    />
                </ErrorBoundary>
                <ErrorBoundary componentName="HomeBottomShelfContainer">
                    <HomeBottomShelfContainer
                        ebDomain={env.ebDomain}
                        tld={env.tld}
                        languageSubdirectory=""
                        localeInfo={env.localeInfo}
                        geoSeoLinksEnabled={splitValues.geoSeoLinksEnabled}
                    />
                </ErrorBoundary>
                <ErrorBoundary componentName="Footer">
                    <Footer {...footerProps} />
                </ErrorBoundary>
            </AppContextProvider>
        </main>
    );
}

const _mapHomeStateToProps = (state: DiscoverState) => {
    return {
        env: state.env,
    };
};

const _mapHomeDispatchToProps = {
    // handleSearch: runSearch,
    handleRestOfContext: getRestOfContext,
    // updateHeaderImage: updateHeaderImage,
};

export const connector = connect(_mapHomeStateToProps, _mapHomeDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export const HomePageContainer = connector(HomePage);
